import { syncState } from "@legendapp/state";
import { nanoid } from "@repo/shared/utils/id";

import { stopActiveStream } from "~/components/app/chat/chat-controller";
import {
  clearCachedMessages,
  setCachedMessages,
} from "~/components/app/chat/message-cache";
import { activeThreadId$, threads$ } from "./threads.store";

/** Starts a new conversation with a locally generated ID. The backend creates the thread on first message. */
export const newThread = () => {
  const id = nanoid();
  // Seed an empty cached transcript so the chat treats this locally created
  // thread as known-empty and renders the suggested-prompts state immediately,
  // rather than showing the hydration skeleton for a thread that doesn't exist
  // on the server yet.
  setCachedMessages(id, []);
  activeThreadId$.set(id);
};

/**
 * Toggles the favorite flag stored in the thread's metadata. The debounced
 * `update` handler persists it via `trpc.ai.updateThread` and the change
 * broadcasts to other tabs over Ably.
 */
export const toggleThreadFavorite = (threadId: string) => {
  const current = threads$[threadId].metadata.get();
  threads$[threadId].metadata.set({
    ...current,
    favorite: !current?.favorite,
  });
};

/**
 * Deletes a thread: aborts its stream if it's the active/streaming one, removes
 * it optimistically (the syncedCrud delete handler owns the server call and
 * retries), clears its cached transcript, and starts a fresh conversation when
 * the deleted thread was active. Callers viewing the deleted thread's page are
 * responsible for navigating away.
 */
export const deleteThread = (threadId: string) => {
  if (activeThreadId$.get() === threadId) stopActiveStream();
  threads$[threadId].delete();
  clearCachedMessages(threadId);
  if (activeThreadId$.get() === threadId) newThread();
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Thread titles are generated asynchronously on the server and normally arrive
// over Ably. Mastra's v6 stream path fires title generation detached/unawaited,
// so the title can land on an unbounded timeline well after the turn finishes
// (slow/cold/rate-limited model calls). Ably realtime has no replay, so a
// broadcast published during a subscription gap is otherwise lost until reload.
// This backoff schedule re-syncs the threads loader until the title appears,
// up to ~27s total, early-exiting as soon as a title is present (e.g. when a
// fast Ably broadcast already merged it in).
const TITLE_REFRESH_DELAYS_MS = [0, 1000, 2000, 3000, 5000, 8000, 8000];

/**
 * Refreshes the threads list until the given thread has a title (or attempts
 * are exhausted). Used after a chat turn finishes so a freshly generated title
 * populates without requiring a page reload, even if the Ably broadcast was
 * missed or title generation is slow.
 */
export const refreshThreadTitle = async (threadId: string): Promise<void> => {
  for (const delay of TITLE_REFRESH_DELAYS_MS) {
    if (threads$[threadId].get()?.title) return;
    if (delay > 0) await sleep(delay);
    try {
      await syncState(threads$).sync();
    } catch {
      // Swallow transient sync failures; the next attempt (or Ably) recovers.
    }
    if (threads$[threadId].get()?.title) return;
  }
};
